import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';

import { crmApi, errMsg } from '@crm/api';
import { salesActions, salesSelectors, salesThunks } from '@crm/features/sales/salesSlice';
import { goodsSelectors, goodsThunks } from '@crm/features/goods/goodsSlice';
import { directionsSelectors, directionsThunks } from '@crm/features/directions/directionsSlice';
import { UiAutosuggest, UiButton, UiCheckbox, UiInput, UiSelect } from '@shared/common/ui';
import Icon from '@shared/common/icon/Icon';
import { formatMoscowDateTime, formatMoscowTime, todayDateOnly } from '@shared/helpers/dates';
import { QrPaymentModal } from '@crm/components/QrPaymentModal';

function fmt(v) {
  return new Intl.NumberFormat('ru-RU').format(v ?? 0) + ' ₽';
}

function today() {
  return todayDateOnly();
}

function fmtDateTime(v) {
  return formatMoscowDateTime(v) || '—';
}

function fmtTime(v) {
  return formatMoscowTime(v) || '—';
}

const STATUS_LABELS = {
  0: 'Отменена',
  1: 'Ожидает оплаты',
  2: 'Оплачена',
  3: 'Возврат',
};

const PAYMENT_TYPE_LABELS = {
  1: 'QR',
  2: 'Наличные',
  3: 'Подарочная карта',
  4: 'ПК доплата наличные',
  5: 'ПК доплата QR',
};

const PAYMENT_TYPES = {
  QR: 1,
  CASH: 2,
  GIFT: 3,
  GIFT_CASH: 4,
  GIFT_QR: 5,
};

const PAYMENT_TYPE_OPTIONS = [
  { value: PAYMENT_TYPES.CASH, label: 'Наличные' },
  { value: PAYMENT_TYPES.QR, label: 'QR' },
  { value: PAYMENT_TYPES.GIFT, label: 'Подарочная карта' },
  { value: PAYMENT_TYPES.GIFT_CASH, label: 'ПК доплата наличные' },
  { value: PAYMENT_TYPES.GIFT_QR, label: 'ПК доплата QR' },
];

const GIFT_PAYMENT_TYPES = new Set([
  PAYMENT_TYPES.GIFT,
  PAYMENT_TYPES.GIFT_CASH,
  PAYMENT_TYPES.GIFT_QR,
]);

const SALE_ITEM_KIND = {
  SERVICE: 'service',
  GOODS: 'goods',
  GIFT: 'gift',
};

function isGiftCardGoods(goods) {
  const hay = `${goods?.name ?? ''} ${goods?.articleNumber ?? ''}`.toLowerCase();
  return /подар|сертифик|gift|certificate|card/.test(hay);
}

function matchesQuery(item, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return `${item?.name ?? ''} ${item?.articleNumber ?? ''}`.toLowerCase().includes(q);
}

function goodsPrice(goods) {
  return Number(goods?.price ?? goods?.defaultPrice ?? 0) || 0;
}

function generateGiftCardNumber() {
  return String(Math.floor(10000 + Math.random() * 90000));
}

function discountValueByPrice(price, count, saleType, saleAmount) {
  const base = (Number(price) || 0) * (Number(count) || 0);
  const discount = Number(saleAmount) || 0;
  if (discount <= 0) return 0;
  if (Number(saleType) === 1) return Math.min(base, (base / 100) * discount);
  if (Number(saleType) === 2) return Math.min(base, discount);
  return 0;
}

function lineAmount(price, count, saleType, saleAmount) {
  const base = (Number(price) || 0) * (Number(count) || 0);
  return Math.max(0, base - discountValueByPrice(price, count, saleType, saleAmount));
}

function clientDiscount(client) {
  const saleType = Number(client?.saleType);
  const saleAmount = Number(client?.saleAmount) || 0;
  if ((saleType !== 1 && saleType !== 2) || saleAmount <= 0) {
    return { saleType: 1, saleAmount: 0 };
  }
  return { saleType, saleAmount };
}

function discountCaption(saleType, saleAmount) {
  if (Number(saleType) === 1) return `${Number(saleAmount)}%`;
  return fmt(Number(saleAmount));
}

function tariffOptionLabel(tariff, client) {
  const name = tariff.tariffName || 'Тариф';
  const price = Number(tariff.price ?? 0) || 0;
  // Legacy-правило: постоянная скидка действует только на абонементы (count > 1).
  if (Number(tariff.tariffCount) <= 1) return `${name} · ${fmt(price)}`;
  const discount = clientDiscount(client);
  const discountByPrice = discountValueByPrice(
    price,
    1,
    discount.saleType,
    discount.saleAmount,
  );

  if (discountByPrice <= 0) return `${name} · ${fmt(price)}`;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
      <span>{name} ·</span>
      <strong>{fmt(price - discountByPrice)}</strong>
      <del className="muted">{fmt(price)}</del>
      <span className="muted">скидка {discountCaption(discount.saleType, discount.saleAmount)}</span>
    </span>
  );
}

function saleItemBaseAmount(item) {
  return (Number(item?.price) || 0) * (Number(item?.count) || 0);
}

function getDiscountValidationError(items) {
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const saleAmount = Number(item.saleAmount) || 0;
    const saleType = Number(item.saleType) || 1;
    const baseAmount = saleItemBaseAmount(item);
    const itemName = item.name || `позиция ${i + 1}`;

    if (saleAmount < 0) {
      return `Скидка для «${itemName}» не может быть отрицательной`;
    }
    if (saleType === 1 && saleAmount > 100) {
      return `Скидка в процентах для «${itemName}» не может быть больше 100%`;
    }
    if (saleType === 2 && saleAmount > baseAmount) {
      return `Скидка в рублях для «${itemName}» не может быть больше стоимости позиции`;
    }
  }
  return null;
}

function SaleSearchCombobox({
  label,
  value,
  onChange,
  options,
  onSelect,
  placeholder,
  emptyText = 'Ничего не найдено',
  getOptionKey = (item) => item.id,
  getOptionLabel = (item) => item.name,
  onClear,
  disabled = false,
  showChevron = true,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <label className="ui-field" ref={rootRef}>
      <span className="ui-field__label">{label}</span>
      <div className="ui-autosuggest">
        <input
          value={value}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          placeholder={placeholder}
          className="ui-control ui-autosuggest__input"
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
        />
        {showChevron ? (
          <span className={`ui-autosuggest__chevron ${open ? 'is-open' : ''}`} aria-hidden>
            ▾
          </span>
        ) : null}
        {value ? (
          <button
            type="button"
            className="ui-autosuggest__clear"
            aria-label="Очистить"
            onClick={() => {
              onClear?.();
              setOpen(true);
            }}
          >
            ×
          </button>
        ) : null}
        {open ? (
          <ul className="ui-autosuggest__list" role="listbox">
            {options.length ? (
              options.map((item) => (
                <li
                  key={String(getOptionKey(item))}
                  className="ui-autosuggest__item"
                  role="option"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onSelect(item);
                    setOpen(false);
                  }}
                >
                  {getOptionLabel(item)}
                </li>
              ))
            ) : (
              <li className="ui-autosuggest__empty">{emptyText}</li>
            )}
          </ul>
        ) : null}
      </div>
    </label>
  );
}

export function SalePage() {
  const { branchId } = useParams();
  const dispatch = useDispatch();

  const list = useSelector(salesSelectors.selectList(branchId));
  const [creating, setCreating] = useState(false);
  const [qrPayment, setQrPayment] = useState(null);
  const [day, setDay] = useState(today());
  const [detailsModal, setDetailsModal] = useState({
    open: false,
    loading: false,
    error: null,
    sale: null,
    chequeUrl: null,
    chequeError: null,
  });

  useEffect(() => {
    if (branchId) dispatch(salesThunks.list({ branchId, params: { day } }));
  }, [dispatch, branchId, day]);

  const openDetails = async (saleId) => {
    setDetailsModal({
      open: true,
      loading: true,
      error: null,
      sale: null,
      chequeUrl: null,
      chequeError: null,
    });

    try {
      const [{ data: saleData }, chequeResult] = await Promise.all([
        crmApi(branchId).get(`/sales/${saleId}`),
        crmApi(branchId)
          .get(`/sales/${saleId}/cheque`)
          .then(({ data }) => ({ url: data?.url ?? null, error: null }))
          .catch((e) => ({ url: null, error: errMsg(e, 'Чек не найден') })),
      ]);

      setDetailsModal({
        open: true,
        loading: false,
        error: null,
        sale: saleData.sale ?? null,
        chequeUrl: chequeResult.url,
        chequeError: chequeResult.error,
      });
    } catch (e) {
      setDetailsModal((prev) => ({
        ...prev,
        loading: false,
        error: errMsg(e, 'Не удалось загрузить продажу'),
      }));
    }
  };

  const closeDetails = () => {
    setDetailsModal({
      open: false,
      loading: false,
      error: null,
      sale: null,
      chequeUrl: null,
      chequeError: null,
    });
  };

  return (
    <section>
      <header className="page-header">
        <h1>Касса</h1>
        <UiInput
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          style={{ marginLeft: 16 }}
        />
        <div className="page-header__spacer" />
        <UiButton type="button" onClick={() => setCreating(true)}>
          Новая продажа
        </UiButton>
      </header>

      {creating && (
        <SaleForm
          branchId={branchId}
          onCancel={() => {
            setCreating(false);
            dispatch(salesActions.resetSave(branchId));
          }}
          onCreated={(sale) => {
            setCreating(false);
            if (sale?.paymentUrl) {
              setQrPayment({
                paymentUrl: sale.paymentUrl,
                qrCode: sale.qrCode,
                amount: sale.amount,
                paymentCode: sale.paymentCode,
              });
            }
            dispatch(salesThunks.list({ branchId, params: { day } }));
          }}
        />
      )}

      <div className="form-card" style={{ marginTop: 16 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Время</th>
              <th>Клиент</th>
              <th>Статус</th>
              <th style={{ textAlign: 'right' }}>Сумма</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id}>
                <td>{fmtTime(s.soldAt)}</td>
                <td>{s.clientName ?? s.clientId ?? 'Без клиента'}</td>
                <td>{STATUS_LABELS[s.status] ?? s.status}</td>
                <td style={{ textAlign: 'right' }}>{fmt(s.amount)}</td>
                <td style={{ textAlign: 'right' }}>
                  <UiButton variant="ghost" size="sm" type="button" onClick={() => openDetails(s.id)}>
                    Детали
                  </UiButton>
                </td>
              </tr>
            ))}
            {!list.length && (
              <tr>
                <td colSpan={5} style={{ color: 'var(--color-muted)', textAlign: 'center' }}>
                  Продаж за {day} пока нет
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detailsModal.open ? (
        <SaleDetailsModal
          modal={detailsModal}
          onClose={closeDetails}
        />
      ) : null}
      {qrPayment ? (
        <QrPaymentModal payment={qrPayment} onClose={() => setQrPayment(null)} />
      ) : null}
    </section>
  );
}

function SaleDetailsModal({ modal, onClose }) {
  const sale = modal.sale;

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-card clients-page__sale-modal" role="dialog" aria-modal="true" aria-labelledby="sale-details-title">
        <div className="modal-card__header clients-page__sale-modal-header">
          <h2 id="sale-details-title">Детали продажи</h2>
          <button type="button" className="clients-page__icon-btn" aria-label="Закрыть" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-card__body">
          {modal.loading ? <div className="placeholder">Загрузка…</div> : null}
          {modal.error ? <div className="form-error">{modal.error}</div> : null}

          {!modal.loading && !modal.error && sale ? (
            <>
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div>
                  <div className="muted">Покупатель</div>
                  <strong>{sale.clientName ?? sale.clientId ?? 'Без клиента'}</strong>
                </div>
                <div>
                  <div className="muted">Статус</div>
                  <strong>{STATUS_LABELS[sale.status] ?? sale.status}</strong>
                </div>
                <div>
                  <div className="muted">Дата и время</div>
                  <strong>{fmtDateTime(sale.soldAt)}</strong>
                </div>
                <div>
                  <div className="muted">Сумма</div>
                  <strong>{fmt(sale.amount)}</strong>
                </div>
                <div>
                  <div className="muted">Тип оплаты</div>
                  <strong>{PAYMENT_TYPE_LABELS[sale.type] ?? `Тип ${sale.type}`}</strong>
                </div>
                {sale.gift ? (
                  <div>
                    <div className="muted">Номер подарочной карты</div>
                    <strong>{sale.gift}</strong>
                  </div>
                ) : null}
                {sale.giftIsUse ? (
                  <div>
                    <div className="muted">Оплачено подарочной картой</div>
                    <strong>{sale.giftIsUse}</strong>
                  </div>
                ) : null}
                <div>
                  <div className="muted">Продавец</div>
                  <strong>{sale.managerName || 'Через ЛК самостоятельно'}</strong>
                </div>
              </div>

              <h3 style={{ marginTop: 0 }}>Позиции</h3>
              {sale.items?.length ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Товар / услуга</th>
                      <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Цена</th>
                      <th style={{ whiteSpace: 'nowrap' }}>Кол-во</th>
                      <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sale.items.map((it) => (
                      <tr key={it.id}>
                        <td>
                          <div>{it.name || it.goodsName || it.directionName || '—'}</div>
                          {it.goodsName || it.directionName || it.tariffName || it.goodsId || it.directionId || it.tariffId ? (
                            <div className="muted" style={{ fontSize: 12 }}>
                              {it.goodsName || it.goodsId ? `Товар: ${it.goodsName || it.goodsId}` : ''}
                              {it.directionName || it.directionId
                                ? `${it.goodsName || it.goodsId ? ' · ' : ''}Направление: ${it.directionName || it.directionId}`
                                : ''}
                              {it.tariffName || it.tariffId
                                ? `${it.goodsName || it.goodsId || it.directionName || it.directionId ? ' · ' : ''}Тариф: ${it.tariffName || it.tariffId}`
                                : ''}
                            </div>
                          ) : null}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{it.price != null ? fmt(it.price) : '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{it.count}</td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(it.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="muted">Нет позиций</div>
              )}

              <div className="modal-card__actions">
                {modal.chequeUrl ? (
                  <UiButton type="button" onClick={() => window.open(modal.chequeUrl, '_blank', 'noopener,noreferrer')}>
                    Открыть чек
                  </UiButton>
                ) : null}
                {!modal.chequeUrl && modal.chequeError ? (
                  <span className="muted">{modal.chequeError}</span>
                ) : null}
                <UiButton variant="ghost" type="button" onClick={onClose}>
                  Закрыть
                </UiButton>
              </div>
            </>
          ) : null}
          {!modal.loading && modal.error ? (
            <div className="modal-card__actions">
              <UiButton variant="ghost" type="button" onClick={onClose}>
                Закрыть
              </UiButton>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SaleForm({ branchId, onCancel, onCreated }) {
  const dispatch = useDispatch();
  const saveStatus = useSelector(salesSelectors.selectSaveStatus(branchId));
  const saveError = useSelector(salesSelectors.selectSaveError(branchId));

  const goodsList = useSelector(goodsSelectors.selectList(branchId));
  const directionsList = useSelector(directionsSelectors.selectList(branchId));
  const [itemKind, setItemKind] = useState(SALE_ITEM_KIND.SERVICE);
  const [directionQuery, setDirectionQuery] = useState('');
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [directionDetail, setDirectionDetail] = useState(null);
  const [directionLoading, setDirectionLoading] = useState(false);
  const [directionError, setDirectionError] = useState(null);
  const [selectedTariffId, setSelectedTariffId] = useState('');
  const [goodsQuery, setGoodsQuery] = useState('');
  const [selectedGoods, setSelectedGoods] = useState(null);
  const [giftAmount, setGiftAmount] = useState('');
  const [draftError, setDraftError] = useState(null);
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPES.CASH);
  const [skipPaymentProvider, setSkipPaymentProvider] = useState(false);
  const [giftCardNumber, setGiftCardNumber] = useState('');
  const [giftCardData, setGiftCardData] = useState(null);
  const [giftCardLoading, setGiftCardLoading] = useState(false);
  const [giftCardError, setGiftCardError] = useState(null);
  const [giftCardWarning, setGiftCardWarning] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    if (!goodsList.length) dispatch(goodsThunks.list({ branchId, params: { limit: 500 } }));
    if (!directionsList.length) dispatch(directionsThunks.list({ branchId, params: { limit: 500 } }));
  }, [dispatch, branchId, goodsList.length, directionsList.length]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      clientId: '',
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const items = useWatch({ control, name: 'items', defaultValue: [] });
  const normalGoods = useMemo(() => goodsList.filter((g) => !isGiftCardGoods(g)), [goodsList]);
  const directionSuggestions = useMemo(
    () => directionsList.filter((d) => matchesQuery(d, directionQuery)).slice(0, 20),
    [directionsList, directionQuery],
  );
  const goodsSuggestions = useMemo(
    () => normalGoods.filter((g) => matchesQuery(g, goodsQuery)).slice(0, 20),
    [normalGoods, goodsQuery],
  );
  const selectedTariff = useMemo(
    () => directionDetail?.tariffs?.find((t) => t.tariffId === selectedTariffId) ?? null,
    [directionDetail, selectedTariffId],
  );
  const permanentDiscount = clientDiscount(selectedClient);
  const total = useMemo(
    () => items.reduce((acc, it) => acc + lineAmount(it.price, it.count, it.saleType, it.saleAmount), 0),
    [items],
  );
  const grossTotal = useMemo(
    () => items.reduce((acc, it) => acc + saleItemBaseAmount(it), 0),
    [items],
  );
  const hasGiftCardSaleItem = useMemo(
    () => items.some((it) => it.kind === SALE_ITEM_KIND.GIFT),
    [items],
  );
  const paymentOptions = useMemo(
    () =>
      hasGiftCardSaleItem
        ? PAYMENT_TYPE_OPTIONS.filter((opt) => opt.value === PAYMENT_TYPES.CASH || opt.value === PAYMENT_TYPES.QR)
        : PAYMENT_TYPE_OPTIONS,
    [hasGiftCardSaleItem],
  );

  useEffect(() => {
    if (!hasGiftCardSaleItem || !GIFT_PAYMENT_TYPES.has(Number(paymentType))) return;
    setPaymentType(PAYMENT_TYPES.CASH);
    setGiftCardNumber('');
    setGiftCardData(null);
    setGiftCardError(null);
    setGiftCardWarning(null);
  }, [hasGiftCardSaleItem, paymentType]);

  const applyGiftCardDiscounts = (nominal) => {
    let rest = Number(nominal) || 0;
    items.forEach((item, idx) => {
      const base = saleItemBaseAmount(item);
      const discount = Math.min(base, rest);
      rest = Math.max(0, rest - discount);
      setValue(`items.${idx}.saleType`, 2, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      setValue(`items.${idx}.saleAmount`, discount, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      setValue(`items.${idx}.saleAmountByPrice`, discount, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      setValue(`items.${idx}.amount`, Math.max(0, base - discount), {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    });
  };

  const clearGiftCardDiscounts = () => {
    const permanentDiscount = clientDiscount(selectedClient);
    items.forEach((item, idx) => {
      if (item.kind === SALE_ITEM_KIND.GIFT) return;
      const itemDiscount = item.permanentDiscountEligible
        ? permanentDiscount
        : { saleType: 1, saleAmount: 0 };
      const base = saleItemBaseAmount(item);
      const discountByPrice = discountValueByPrice(
        item.price,
        item.count,
        itemDiscount.saleType,
        itemDiscount.saleAmount,
      );
      setValue(`items.${idx}.saleType`, itemDiscount.saleType, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      setValue(`items.${idx}.saleAmount`, itemDiscount.saleAmount, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      setValue(`items.${idx}.saleAmountByPrice`, discountByPrice, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      setValue(`items.${idx}.amount`, Math.max(0, base - discountByPrice), {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    });
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    if (giftCardData) return;
    const permanentDiscount = clientDiscount(client);
    items.forEach((item, idx) => {
      if (item.kind === SALE_ITEM_KIND.GIFT) return;
      const itemDiscount = item.permanentDiscountEligible
        ? permanentDiscount
        : { saleType: 1, saleAmount: 0 };
      const discountByPrice = discountValueByPrice(
        item.price,
        item.count,
        itemDiscount.saleType,
        itemDiscount.saleAmount,
      );
      setValue(`items.${idx}.saleType`, itemDiscount.saleType, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      setValue(`items.${idx}.saleAmount`, itemDiscount.saleAmount, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      setValue(`items.${idx}.saleAmountByPrice`, discountByPrice, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      setValue(`items.${idx}.amount`, Math.max(0, saleItemBaseAmount(item) - discountByPrice), {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    });
  };

  useEffect(() => {
    if (!GIFT_PAYMENT_TYPES.has(Number(paymentType))) {
      if (giftCardData) clearGiftCardDiscounts();
      setGiftCardData(null);
      setGiftCardError(null);
      setGiftCardWarning(null);
      return undefined;
    }

    const number = giftCardNumber.trim();
    if (!number) {
      if (giftCardData) clearGiftCardDiscounts();
      setGiftCardData(null);
      setGiftCardError(null);
      setGiftCardWarning(null);
      return undefined;
    }

    if (!/^\d+$/.test(number)) {
      if (giftCardData) clearGiftCardDiscounts();
      setGiftCardData(null);
      setGiftCardError('Номер карты должен содержать только цифры');
      setGiftCardWarning(null);
      return undefined;
    }

    const t = setTimeout(async () => {
      setGiftCardLoading(true);
      setGiftCardError(null);
      setGiftCardWarning(null);
      try {
        const { data } = await crmApi(branchId).get('/sales/gift-card/check', {
          params: { number },
        });
        const card = data.card;
        const amount = Number(card?.amount ?? 0);
        setGiftCardData({ ...card, amount });
        applyGiftCardDiscounts(amount);
        if (amount > grossTotal) {
          setGiftCardWarning('Остаток сгорит. Предупредите клиента и посоветуйте добавить еще позицию.');
        }
      } catch (e) {
        if (giftCardData) clearGiftCardDiscounts();
        setGiftCardData(null);
        setGiftCardError(errMsg(e, 'Подарочная карта не найдена или уже использована'));
      } finally {
        setGiftCardLoading(false);
      }
    }, 350);

    return () => clearTimeout(t);
  }, [branchId, giftCardNumber, grossTotal, paymentType, setValue]);

  const onSubmit = async (values) => {
    if (!values.clientId) {
      setDraftError('Выберите клиента');
      return;
    }
    if (!values.items?.length) {
      setDraftError('Добавьте хотя бы одну позицию');
      return;
    }
    const invalidGift = values.items.some(
      (it) => it.kind === SALE_ITEM_KIND.GIFT && Number(it.amount || it.price || 0) <= 0,
    );
    if (invalidGift) {
      setDraftError('Для подарочной карты укажите сумму больше 0 ₽');
      return;
    }
    const discountError = getDiscountValidationError(values.items);
    if (discountError) {
      setDraftError(discountError);
      return;
    }
    if (hasGiftCardSaleItem && GIFT_PAYMENT_TYPES.has(Number(paymentType))) {
      setDraftError('Если в корзине есть подарочная карта, оплата возможна только наличными или QR');
      return;
    }
    if (GIFT_PAYMENT_TYPES.has(Number(paymentType))) {
      if (!giftCardNumber.trim() || !giftCardData || giftCardError) {
        setDraftError('Введите корректный номер подарочной карты');
        return;
      }
      if (Number(paymentType) === PAYMENT_TYPES.GIFT && total > 0) {
        setDraftError('Номинала подарочной карты не хватает. Выберите тип оплаты с доплатой.');
        return;
      }
    }
    const giftSaleItem = values.items.find((it) => it.kind === SALE_ITEM_KIND.GIFT);
    if (giftSaleItem && !giftSaleItem.giftNumber) {
      setDraftError('Для подарочной карты не сгенерирован номер. Удалите позицию и добавьте карту заново.');
      return;
    }
    const payload = {
      clientId: values.clientId || null,
      amount: total,
      type: Number(paymentType),
      skipPaymentProvider,
      gift: giftSaleItem?.giftNumber ?? null,
      giftIsUse: GIFT_PAYMENT_TYPES.has(Number(paymentType)) ? giftCardNumber.trim() : null,
      items: values.items.map((it) => ({
        name: it.name?.trim() || null,
        count: Number(it.count) || 1,
        price: it.price === '' ? null : Number(it.price),
        amount: lineAmount(it.price, it.count, it.saleType, it.saleAmount),
        saleType: it.saleType == null || it.saleType === '' ? null : Number(it.saleType),
        saleAmount: it.saleAmount == null || it.saleAmount === '' ? null : Number(it.saleAmount),
        saleAmountByPrice:
          it.saleAmountByPrice == null || it.saleAmountByPrice === ''
            ? null
            : discountValueByPrice(it.price, it.count, it.saleType, it.saleAmount),
        goodsId: it.goodsId || null,
        directionId: it.directionId || null,
        tariffId: it.tariffId || null,
        goodsType: it.goodsType == null || it.goodsType === '' ? null : Number(it.goodsType),
      })),
    };
    const res = await dispatch(salesThunks.create({ branchId, payload }));
    if (res.meta.requestStatus === 'fulfilled') onCreated(res.payload?.item ?? null);
  };

  const resetDraft = (nextKind = itemKind) => {
    setItemKind(nextKind);
    setDirectionQuery('');
    setSelectedDirection(null);
    setDirectionDetail(null);
    setDirectionError(null);
    setSelectedTariffId('');
    setGoodsQuery('');
    setSelectedGoods(null);
    setGiftAmount('');
    setDraftError(null);
  };

  const selectDirection = async (direction) => {
    setDraftError(null);
    setSelectedDirection(direction);
    setDirectionQuery(direction.name);
    setSelectedTariffId('');
    setDirectionDetail(null);
    setDirectionError(null);
    setDirectionLoading(true);
    try {
      const { data } = await crmApi(branchId).get(`/directions/${direction.id}`);
      setDirectionDetail(data.direction ?? null);
    } catch (e) {
      setDirectionError(errMsg(e, 'Не удалось загрузить тарифы услуги'));
    } finally {
      setDirectionLoading(false);
    }
  };

  const addServiceItem = () => {
    setDraftError(null);
    if (!selectedDirection) {
      setDraftError('Выберите услугу');
      return;
    }
    if (!selectedTariff) {
      setDraftError('Выберите тариф');
      return;
    }
    const price = Number(selectedTariff.price ?? 0) || 0;
    const permanentDiscountEligible = Number(selectedTariff.tariffCount) > 1;
    const permanentDiscount = permanentDiscountEligible
      ? clientDiscount(selectedClient)
      : { saleType: 1, saleAmount: 0 };
    const saleAmountByPrice = discountValueByPrice(
      price,
      1,
      permanentDiscount.saleType,
      permanentDiscount.saleAmount,
    );
    append({
      kind: SALE_ITEM_KIND.SERVICE,
      name: `${selectedDirection.name} / ${selectedTariff.tariffName || 'Тариф'}`,
      count: 1,
      price,
      amount: Math.max(0, price - saleAmountByPrice),
      saleType: permanentDiscount.saleType,
      saleAmount: permanentDiscount.saleAmount,
      saleAmountByPrice,
      goodsId: '',
      directionId: selectedDirection.id,
      tariffId: selectedTariff.tariffId,
      permanentDiscountEligible,
      goodsType: '',
    });
    resetDraft(SALE_ITEM_KIND.SERVICE);
  };

  const addGoodsItem = () => {
    setDraftError(null);
    if (!selectedGoods) {
      setDraftError('Выберите товар');
      return;
    }
    const price = goodsPrice(selectedGoods);
    append({
      kind: SALE_ITEM_KIND.GOODS,
      name: selectedGoods.name,
      count: 1,
      price,
      amount: price,
      saleType: 1,
      saleAmount: 0,
      saleAmountByPrice: 0,
      goodsId: selectedGoods.id,
      directionId: '',
      tariffId: '',
      permanentDiscountEligible: false,
      goodsType: '',
    });
    resetDraft(SALE_ITEM_KIND.GOODS);
  };

  const addGiftItem = () => {
    setDraftError(null);
    const amount = Number(giftAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setDraftError('Для подарочной карты укажите сумму больше 0 ₽');
      return;
    }
    if (items.some((it) => it.kind === SALE_ITEM_KIND.GIFT)) {
      setDraftError('В одной продаже можно добавить только одну подарочную карту');
      return;
    }
    append({
      kind: SALE_ITEM_KIND.GIFT,
      name: 'Подарочная карта',
      giftNumber: generateGiftCardNumber(),
      count: 1,
      price: amount,
      amount,
      saleType: 1,
      saleAmount: 0,
      saleAmountByPrice: 0,
      goodsId: '',
      directionId: '',
      tariffId: '',
      goodsType: 3,
    });
    resetDraft(SALE_ITEM_KIND.GIFT);
  };

  const updateItemAmount = (idx, patch = {}) => {
    const current = { ...items[idx], ...patch };
    const count = Number(current.count) || 0;
    const price = Number(current.price) || 0;
    const saleType = Number(current.saleType) || 1;
    const saleAmount = Number(current.saleAmount) || 0;
    const saleAmountByPrice = discountValueByPrice(price, count, saleType, saleAmount);
    setValue(`items.${idx}.saleAmountByPrice`, saleAmountByPrice, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    setValue(`items.${idx}.amount`, lineAmount(price, count, saleType, saleAmount), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  return (
    <form className="form-card" onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 16, maxWidth: 960 }}>
      <h3>Новая продажа</h3>

      <div className="form-grid">
        <div className="field">
          <label>Клиент</label>
          <Controller
            control={control}
            name="clientId"
            render={({ field }) => (
              <ClientPicker branchId={branchId} {...field} onClientSelect={selectClient} />
            )}
          />
          {!selectedClient ? (
            <span className={draftError === 'Выберите клиента' ? 'form-error' : 'muted'}>
              {draftError === 'Выберите клиента'
                ? draftError
                : 'Клиент обязателен — без него платёж не создаётся'}
            </span>
          ) : null}
        </div>
        {permanentDiscount.saleAmount > 0 ? (
          <div className="form-note form-note--success" style={{ alignSelf: 'end' }}>
            Постоянная скидка клиента: {discountCaption(permanentDiscount.saleType, permanentDiscount.saleAmount)}.
            В списке тарифов цена показана уже с учётом скидки.
          </div>
        ) : null}
      </div>

      <h4>Добавить позицию</h4>
      <div style={{ margin: '8px 0 16px', padding: 12, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <UiButton
            type="button"
            variant={itemKind === SALE_ITEM_KIND.SERVICE ? undefined : 'ghost'}
            onClick={() => resetDraft(SALE_ITEM_KIND.SERVICE)}
          >
            Услуга
          </UiButton>
          <UiButton
            type="button"
            variant={itemKind === SALE_ITEM_KIND.GOODS ? undefined : 'ghost'}
            onClick={() => resetDraft(SALE_ITEM_KIND.GOODS)}
          >
            Обычный товар
          </UiButton>
          <UiButton
            type="button"
            variant={itemKind === SALE_ITEM_KIND.GIFT ? undefined : 'ghost'}
            onClick={() => resetDraft(SALE_ITEM_KIND.GIFT)}
          >
            Подарочная карта
          </UiButton>
        </div>

        {itemKind === SALE_ITEM_KIND.SERVICE ? (
          <div className="form-grid">
            <SaleSearchCombobox
              label="Услуга"
              value={directionQuery}
              onChange={(value) => {
                setDirectionQuery(value);
                setSelectedDirection(null);
                setDirectionDetail(null);
                setSelectedTariffId('');
              }}
              options={directionSuggestions}
              onSelect={selectDirection}
              placeholder="Введите название или выберите из списка"
              onClear={() => {
                setDirectionQuery('');
                setSelectedDirection(null);
                setDirectionDetail(null);
                setSelectedTariffId('');
              }}
              getOptionKey={(item) => item.id}
              getOptionLabel={(item) => item.name}
              showChevron={!selectedDirection}
            />
            <UiSelect
              label="Тариф"
              value={selectedTariffId}
              onChange={setSelectedTariffId}
              disabled={!selectedDirection || directionLoading || !directionDetail?.tariffs?.length}
              placeholder={directionLoading ? 'Загрузка тарифов…' : 'Выберите тариф'}
              options={(directionDetail?.tariffs ?? []).map((tariff) => ({
                value: tariff.tariffId,
                label: tariffOptionLabel(tariff, selectedClient),
              }))}
            />
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <UiButton type="button" onClick={addServiceItem} disabled={directionLoading}>
                Добавить
              </UiButton>
            </div>
          </div>
        ) : null}

        {itemKind === SALE_ITEM_KIND.GOODS ? (
          <div className="form-grid">
            <UiAutosuggest
              label="Товар"
              value={selectedGoods ? selectedGoods.name : goodsQuery}
              onChange={(value) => {
                setGoodsQuery(value);
                setSelectedGoods(null);
              }}
              suggestions={goodsSuggestions}
              onSelect={(goods) => {
                setSelectedGoods(goods);
                setGoodsQuery(goods.name);
                setDraftError(null);
              }}
              placeholder="Начните вводить название товара"
              onClear={() => {
                setGoodsQuery('');
                setSelectedGoods(null);
              }}
              getOptionKey={(item) => item.id}
              getOptionLabel={(item) => `${item.name} · ${fmt(goodsPrice(item))}`}
            />
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <UiButton type="button" onClick={addGoodsItem}>
                Добавить
              </UiButton>
            </div>
          </div>
        ) : null}

        {itemKind === SALE_ITEM_KIND.GIFT ? (
          <div className="form-grid">
            <UiInput
              label="Сумма карты"
              type="number"
              min="1"
              step="0.01"
              value={giftAmount}
              onChange={(e) => setGiftAmount(e.target.value)}
              placeholder="0 ₽"
            />
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <UiButton type="button" onClick={addGiftItem}>
                Добавить
              </UiButton>
            </div>
          </div>
        ) : null}

        {directionError ? <div className="form-error">{directionError}</div> : null}
        {draftError ? <div className="form-error">{draftError}</div> : null}
      </div>

      <h4>Позиции</h4>
      <table className="data-table">
        <thead>
          <tr>
            <th style={{ width: '38%' }}>Наименование</th>
            <th style={{ width: 120, whiteSpace: 'nowrap' }}>Кол-во</th>
            <th style={{ width: 150, whiteSpace: 'nowrap' }}>Цена</th>
            <th style={{ width: 170 }}>Скидка</th>
            <th style={{ width: 120 }}>Сумма</th>
            <th />
          </tr>
        </thead>
        <tbody>
	          {fields.map((f, idx) => (
	            <tr key={f.id}>
              <td>
                <input type="hidden" defaultValue={f.kind} {...register(`items.${idx}.kind`)} />
                <input type="hidden" defaultValue={f.name} {...register(`items.${idx}.name`)} />
                <input type="hidden" defaultValue={f.giftNumber} {...register(`items.${idx}.giftNumber`)} />
                <input type="hidden" defaultValue={f.goodsId} {...register(`items.${idx}.goodsId`)} />
                <input type="hidden" defaultValue={f.directionId} {...register(`items.${idx}.directionId`)} />
                <input type="hidden" defaultValue={f.tariffId} {...register(`items.${idx}.tariffId`)} />
                <input type="hidden" defaultValue={f.goodsType} {...register(`items.${idx}.goodsType`)} />
                <input type="hidden" defaultValue={f.saleAmountByPrice} {...register(`items.${idx}.saleAmountByPrice`, { valueAsNumber: true })} />
                <input type="hidden" defaultValue={f.amount} {...register(`items.${idx}.amount`, { valueAsNumber: true })} />
                {items[idx]?.name || '—'}
                {items[idx]?.kind === SALE_ITEM_KIND.GIFT ? (
                  <div style={{ marginTop: 8 }}>
                    <div className="muted" style={{ fontSize: 12 }}>
                      Подарочная карта
                    </div>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        marginTop: 6,
                        padding: '6px 10px',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-primary)',
                        color: '#fff',
                        fontSize: 18,
                        fontWeight: 800,
                        letterSpacing: 1,
                      }}
                    >
                      № {items[idx]?.giftNumber || f.giftNumber}
                    </div>
                  </div>
                ) : null}
              </td>
              <td>
                <UiInput
                  type="number"
                  min={1}
                  {...register(`items.${idx}.count`, {
                    valueAsNumber: true,
                    onChange: (e) => {
                      const count = Number(e.target.value) || 0;
                      const price = Number(items[idx]?.price) || 0;
                      updateItemAmount(idx, { count, price });
                    },
                  })}
                  style={{ width: 104, minWidth: 104 }}
                  readOnly={items[idx]?.kind === SALE_ITEM_KIND.GIFT}
                />
              </td>
              <td>
                <UiInput
                  type="number"
                  step="0.01"
                  min={items[idx]?.kind === SALE_ITEM_KIND.GIFT ? 1 : undefined}
                  {...register(`items.${idx}.price`, {
                    valueAsNumber: true,
                    onChange: (e) => {
                      const price = Number(e.target.value) || 0;
                      const count = Number(items[idx]?.count) || 0;
                      updateItemAmount(idx, { count, price });
                    },
                  })}
                  style={{ width: 134, minWidth: 134 }}
                  readOnly={items[idx]?.kind !== SALE_ITEM_KIND.GIFT}
                />
              </td>
	              <td>
	                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <UiInput
                    type="number"
                    min={0}
                    max={Number(items[idx]?.saleType || 1) === 1 ? 100 : saleItemBaseAmount(items[idx])}
                    step="0.01"
                    disabled={items[idx]?.kind === SALE_ITEM_KIND.GIFT}
                    {...register(`items.${idx}.saleAmount`, {
                      valueAsNumber: true,
                      onChange: (e) => {
                        const saleAmount = Number(e.target.value) || 0;
                        updateItemAmount(idx, { saleAmount });
                      },
                    })}
                    style={{ width: 82 }}
                  />
                  <input type="hidden" defaultValue={f.saleType} {...register(`items.${idx}.saleType`, { valueAsNumber: true })} />
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    <UiButton
                      type="button"
                      variant={Number(items[idx]?.saleType || 1) === 1 ? 'primary' : 'ghost'}
	                      size="sm"
                      disabled={items[idx]?.kind === SALE_ITEM_KIND.GIFT}
	                      onClick={() => {
	                        setValue(`items.${idx}.saleType`, 1, {
	                          shouldDirty: true,
	                          shouldValidate: true,
	                          shouldTouch: true,
	                        });
	                        updateItemAmount(idx, { saleType: 1 });
	                      }}
                    >
                      %
                    </UiButton>
                    <UiButton
                      type="button"
                      variant={Number(items[idx]?.saleType || 1) === 2 ? 'primary' : 'ghost'}
	                      size="sm"
                      disabled={items[idx]?.kind === SALE_ITEM_KIND.GIFT}
	                      onClick={() => {
	                        setValue(`items.${idx}.saleType`, 2, {
	                          shouldDirty: true,
	                          shouldValidate: true,
	                          shouldTouch: true,
	                        });
	                        updateItemAmount(idx, { saleType: 2 });
	                      }}
                    >
                      ₽
                    </UiButton>
                  </div>
                </div>
              </td>
              <td>
                <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {fmt(lineAmount(items[idx]?.price, items[idx]?.count, items[idx]?.saleType, items[idx]?.saleAmount))}
                </span>
              </td>
              <td style={{ textAlign: 'right' }}>
                <UiButton
                  variant="danger"
                  size="sm"
                  type="button"
                  aria-label={`Удалить позицию ${items[idx]?.name || idx + 1}`}
                  title="Удалить позицию"
                  onClick={() => remove(idx)}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}
                >
                  <Icon icon="Delete" width={18} height={18} />
                </UiButton>
              </td>
            </tr>
          ))}
          {!fields.length ? (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>
                Добавьте услугу, товар или подарочную карту
              </td>
            </tr>
          ) : null}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ textAlign: 'right', fontWeight: 500 }}>
              Итого:
            </td>
            <td style={{ fontWeight: 700 }}>{fmt(total)}</td>
            <td />
          </tr>
        </tfoot>
      </table>

      {errors.items && <div className="form-error">Проверьте позиции</div>}

      <div className="form-grid" style={{ marginTop: 16 }}>
        <UiSelect
          label="Тип оплаты"
          value={paymentType}
          onChange={(value) => {
            const next = Number(value);
            setPaymentType(next);
            setDraftError(null);
            if (!GIFT_PAYMENT_TYPES.has(next)) {
              if (giftCardData) clearGiftCardDiscounts();
              setGiftCardNumber('');
              setGiftCardData(null);
              setGiftCardError(null);
              setGiftCardWarning(null);
            }
          }}
          options={paymentOptions}
          placeholder="Выберите тип оплаты"
        />

        {GIFT_PAYMENT_TYPES.has(Number(paymentType)) ? (
          <UiInput
            label="Номер карты"
            type="number"
            min="1"
            value={giftCardNumber}
            onChange={(e) => {
              setGiftCardNumber(e.target.value.replace(/\D/g, ''));
              setDraftError(null);
            }}
            placeholder="Введите номер подарочной карты"
          />
        ) : null}
      </div>

      {hasGiftCardSaleItem ? (
        <div className="muted" style={{ marginTop: 8 }}>
          В корзине есть подарочная карта. Доступна только оплата наличными или QR.
        </div>
      ) : null}
      {giftCardLoading ? <div className="placeholder">Проверяем подарочную карту…</div> : null}
      {giftCardData ? (
        <div className="muted" style={{ marginTop: 8 }}>
          Номинал подарочной карты: {fmt(giftCardData.amount)}
        </div>
      ) : null}
      {giftCardWarning ? <div className="form-error">{giftCardWarning}</div> : null}
      {giftCardError ? <div className="form-error">{giftCardError}</div> : null}

      {saveError && <div className="form-error">{saveError}</div>}

      <div className="form-actions">
        <UiCheckbox
          label="В подарок"
          checked={skipPaymentProvider}
          onChange={(e) => setSkipPaymentProvider(e.target.checked)}
          style={{ marginRight: 8 }}
        />
        <UiButton type="submit" disabled={saveStatus === 'loading'}>
          {saveStatus === 'loading' ? 'Создание платежа…' : 'Оплатить'}
        </UiButton>
        <UiButton variant="ghost" type="button" onClick={onCancel}>
          Отмена
        </UiButton>
      </div>
    </form>
  );
}

/**
 * Поиск клиента с async-подсказками. Использует GET /clients?q=...
 */
function ClientPicker({ branchId, value, onChange, onClientSelect }) {
  const [q, setQ] = useState('');
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!q || q.length < 2) {
      setOptions([]);
      return undefined;
    }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await crmApi(branchId).get('/clients', { params: { q, limit: 10 } });
        if (alive) setOptions(data.items ?? []);
      } catch (e) {
        if (alive) setErr(errMsg(e, 'Ошибка поиска'));
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, branchId]);

  if (selected || value) {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span>{selected?.fio ?? 'Выбран клиент'}</span>
        <UiButton variant="ghost" size="sm"
          type="button"
          
          onClick={() => {
            onChange('');
            setSelected(null);
            setQ('');
            onClientSelect?.(null);
          }}
        >
          Сменить
        </UiButton>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <UiInput
        placeholder="Начните вводить ФИО…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {err && <div className="form-error">{err}</div>}
      {options.length > 0 && (
        <ul
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            listStyle: 'none',
            padding: 0,
            margin: 0,
            zIndex: 10,
            maxHeight: 200,
            overflow: 'auto',
          }}
        >
          {options.map((o) => (
            <li
              key={o.id}
              style={{ padding: '8px 12px', cursor: 'pointer' }}
              onClick={() => {
                onChange(o.id);
                setSelected(o);
                onClientSelect?.(o);
              }}
            >
              <span style={{ whiteSpace: 'pre-line' }}>
                {o.suggestLabel || `${o.fio ?? ''}${o.phone ? ` (${o.phone})` : ''}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
